import React, { Component } from "react";
import { Modal, Button, Tabs, notification } from "antd";
import { RocketOutlined, ClearOutlined, BellOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { APP_VERSION_NUMBER, APP_VERSION_STRING } from "../appVersion";

const { TabPane } = Tabs;

const WHATS_NEW = [
  {
    version: 4,
    date:    "June 1, 2026",
    title:   "Home Health Check-Up, Portal Upgrades & Version Notifications",
    features: [
      "🏠 Home Health Check-Up launched — 29 questions, section scores, Buddha happiness meter",
      "🏡 Health check answers auto-populate your Home Buddha portal subsections",
      "😊 Subsection mood indicators show your health check score per area",
      "📷 Camera & upload buttons added to every portal subsection",
      "🏅 Certified inspection CTA added to every section tab",
      "🔔 Version checker now shows What's New + cache clearing instructions",
      "📊 Ad system now supports 36 Home Buddha ad locations",
    ],
  },
  {
    version: 3,
    date:    "May 31, 2026",
    title:   "Home Buddha Portal Ads & Network Improvements",
    features: [
      "🏡 Home Buddha portal supports advertising — sidebar and per-subsection ad slots",
      "🌐 MyNetwork catches all referred users across all referral field variants",
      "📱 SMS verification infrastructure added",
      "📊 Missed Leads supports Agent, Lender, and Vendor assignment",
      "🔗 Share with Client works from the AI site",
    ],
  },
];

const NAVY  = "#1A2280";
const GOLD  = "#D4921A";

const BROWSER_STEPS = [
  {
    name:  "Chrome (Desktop)",
    icon:  "🌐",
    urgent: true,
    steps: [
      "Press Ctrl+Shift+Delete (Windows) or Cmd+Shift+Delete (Mac)",
      "Set time range to 'All time'",
      "Check 'Cached images and files' and 'Cookies and other site data'",
      "Click 'Clear data' — then reload the page",
      "OR: Open DevTools (F12) → right-click the reload button → 'Empty Cache and Hard Reload'",
    ],
  },
  {
    name:  "Chrome (Mobile/Android)",
    icon:  "📱",
    urgent: true,
    steps: [
      "Tap the 3-dot menu (⋮) → Settings → Privacy and security",
      "Tap 'Clear browsing data'",
      "Select 'All time', check Cached images — tap Clear data",
    ],
  },
  {
    name:  "Safari (Mac)",
    icon:  "🧭",
    steps: [
      "Hold Shift and click the Reload button for a quick hard refresh",
      "OR: Safari menu → Settings → Privacy → Manage Website Data → Remove All",
      "OR: Develop menu → Empty Caches (enable Develop in Safari Preferences → Advanced)",
    ],
  },
  {
    name:  "Safari (iPhone/iPad)",
    icon:  "📱",
    steps: [
      "Settings app → Safari → scroll down → Clear History and Website Data",
      "OR: Settings → Safari → Advanced → Website Data → Remove All Website Data",
    ],
  },
  {
    name:  "Firefox",
    icon:  "🦊",
    steps: [
      "Press Ctrl+Shift+Delete → select 'Everything' → check Cache → Clear Now",
      "OR: Ctrl+F5 for a quick hard refresh",
    ],
  },
];

class VersionChecker extends Component {
  state = {
    showModal: false,
    activeTab: "whats_new",
    clearing:  false,
    cleared:   false,
  };

  componentDidMount() {
    // Small delay so page finishes loading first
    setTimeout(this.checkVersion, 1500);
  }

  checkVersion = () => {
    try {
      const stored = parseInt(localStorage.getItem("mhb_portal_version") || "0");
      if (stored === 0) {
        // First visit — store silently, no popup
        localStorage.setItem("mhb_portal_version", String(APP_VERSION_NUMBER));
        return;
      }
      if (stored < APP_VERSION_NUMBER) {
        localStorage.setItem("mhb_portal_version", String(APP_VERSION_NUMBER));
        this.setState({ showModal: true, activeTab: "whats_new" });
      }
    } catch(e) {
      console.warn("[VersionChecker] localStorage error:", e);
    }
  };

  componentDidUpdate(prevProps) {
    // Check for upgrade notice after user logs in
    if (this.props.context?.state?.userDetails && !prevProps.context?.state?.userDetails) {
      this.checkUpgradeNotice();
    }
  }

  checkUpgradeNotice = () => {
    const plan = localStorage.getItem("mhb_show_upgrade_notice");
    if (plan) {
      localStorage.removeItem("mhb_show_upgrade_notice");
      notification.success({
        message:     "🎉 Plan Upgraded!",
        description: `Welcome to ${plan}! Your new features are ready.`,
        duration:    8,
        placement:   "topRight",
      });
    }
  };

  handleClose = () => this.setState({ showModal: false });

  handleClearAndReload = () => {
    this.setState({ clearing: true });
    try {
      // Preserve version marker so popup doesn't re-fire after reload
      const version = localStorage.getItem("mhb_portal_version");
      if ("caches" in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
          if (version) localStorage.setItem("mhb_portal_version", version);
          window.location.reload(true);
        });
      } else {
        if (version) localStorage.setItem("mhb_portal_version", version);
        window.location.reload(true);
      }
    } catch(e) {
      window.location.reload(true);
    }
  };

  // Called from subscription flow to show upgrade notice
  static showUpgradeNotice(planName) {
    localStorage.setItem("mhb_show_upgrade_notice", planName || "Pro");
  }

  renderWhatsNew = () => (
    <div style={{ maxHeight: "420px", overflowY: "auto", paddingRight: "4px" }}>
      {WHATS_NEW.map((release, ri) => (
        <div key={ri} style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
            <div style={{ background: ri === 0 ? NAVY : "#f0f4ff", borderRadius: "8px", padding: "6px 12px" }}>
              <span style={{ fontSize: "11px", fontWeight: 800, color: ri === 0 ? GOLD : "#718096", textTransform: "uppercase", letterSpacing: ".05em" }}>
                {ri === 0 ? "✨ Latest" : `v${release.version}`}
              </span>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "14px", color: NAVY }}>{release.title}</div>
              <div style={{ fontSize: "11px", color: "#718096" }}>{release.date}</div>
            </div>
          </div>
          <div style={{ paddingLeft: "8px" }}>
            {release.features.map((f, fi) => (
              <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "7px" }}>
                <CheckCircleOutlined style={{ color: "#38A169", fontSize: "13px", marginTop: "2px", flexShrink: 0 }} />
                <span style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.5 }}>{f}</span>
              </div>
            ))}
          </div>
          {ri < WHATS_NEW.length - 1 && <div style={{ borderTop: "1px dashed #E2E8F0", marginTop: "16px" }} />}
        </div>
      ))}
    </div>
  );

  renderCacheHelp = () => {
    const { clearing, cleared } = this.state;
    return (
      <div style={{ maxHeight: "420px", overflowY: "auto", paddingRight: "4px" }}>

        {/* Why banner */}
        <div style={{ background: "#fff8f0", border: "1px solid #f6d5a8", borderRadius: "10px", padding: "12px 16px", marginBottom: "14px" }}>
          <div style={{ fontWeight: 700, color: "#744210", fontSize: "13px", marginBottom: "4px" }}>⚠️ Why do I need to clear my cache?</div>
          <div style={{ fontSize: "12px", color: "#7B341E", lineHeight: 1.6 }}>
            Your browser saves a copy of the site to load it faster. When we push updates, your browser may still serve the old version.
            Clearing the cache forces it to download the latest — this fixes most display issues and missing features after an update.
          </div>
        </div>

        {/* One-click button */}
        <Button type="primary" icon={<ClearOutlined />} block loading={clearing} disabled={cleared}
          onClick={this.handleClearAndReload}
          style={{ background: NAVY, borderColor: NAVY, borderRadius: "50px", fontWeight: 700, marginBottom: "8px", height: "44px", fontSize: "14px" }}>
          {cleared ? "✓ Cleared! Reloading..." : clearing ? "Clearing..." : "🔄 Clear Cache & Reload Now (Recommended)"}
        </Button>

        {/* Chrome warning — most common problem */}
        <div style={{ background: "#FFF5F5", border: "1px solid #FEB2B2", borderRadius: "10px", padding: "12px 16px", marginBottom: "14px" }}>
          <div style={{ fontWeight: 700, color: "#C53030", fontSize: "12px", marginBottom: "6px" }}>🌐 Chrome Users — Extra Step Required</div>
          <div style={{ fontSize: "12px", color: "#742A2A", lineHeight: 1.6, marginBottom: "8px" }}>
            Chrome caches aggressively. Even after clearing, you may need to do a <strong>hard refresh</strong>:
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <div style={{ background: "#C53030", color: "white", borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: 700 }}>
              Windows: Ctrl + Shift + R
            </div>
            <div style={{ background: "#C53030", color: "white", borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: 700 }}>
              Mac: Cmd + Shift + R
            </div>
          </div>
        </div>

        <div style={{ fontSize: "12px", color: "#718096", marginBottom: "12px", textAlign: "center" }}>— or follow manual steps for your browser —</div>

        {BROWSER_STEPS.map((browser, bi) => (
          <div key={bi} style={{ marginBottom: "12px", border: `1px solid ${browser.urgent ? "#FEB2B2" : "#E2E8F0"}`, borderRadius: "10px", overflow: "hidden" }}>
            <div style={{ background: browser.urgent ? "#FFF5F5" : "#F7F9FC", padding: "8px 14px", fontWeight: 700, fontSize: "13px", color: browser.urgent ? "#C53030" : NAVY, borderBottom: `1px solid ${browser.urgent ? "#FEB2B2" : "#E2E8F0"}` }}>
              {browser.icon} {browser.name} {browser.urgent ? "⚠️" : ""}
            </div>
            <div style={{ padding: "10px 14px" }}>
              {browser.steps.map((step, si) => (
                <div key={si} style={{ display: "flex", gap: "8px", marginBottom: "6px", alignItems: "flex-start" }}>
                  <span style={{ background: browser.urgent ? "#C53030" : NAVY, color: "white", borderRadius: "50%", width: "18px", height: "18px", fontSize: "10px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px" }}>
                    {si + 1}
                  </span>
                  <span style={{ fontSize: "12px", color: "#4A5568", lineHeight: 1.5 }}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Still stuck? */}
        <div style={{ background: "#F0FFF4", border: "1px solid #9AE6B4", borderRadius: "10px", padding: "12px 16px", marginTop: "4px" }}>
          <div style={{ fontWeight: 700, color: "#276749", fontSize: "12px", marginBottom: "4px" }}>✅ Still seeing old content?</div>
          <div style={{ fontSize: "12px", color: "#4A5568", lineHeight: 1.6 }}>
            Try opening the site in a private/incognito window to bypass all caches. 
            If it works there, your regular browser needs a full cache clear.
            Contact us and let us know which version you see at the bottom of the login screen.
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { showModal, activeTab } = this.state;
    const latest = WHATS_NEW[0];

    return (
      <Modal visible={showModal} onCancel={this.handleClose} footer={null} width={560}
        style={{ top: 40 }} bodyStyle={{ padding: 0 }} className="modal-black-close">

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`, padding: "24px 28px", borderRadius: "8px 8px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}>
            <RocketOutlined style={{ color: GOLD, fontSize: "24px" }} />
            <div>
              <div style={{ color: "white", fontWeight: 800, fontSize: "18px" }}>My Home Buddha Updated</div>
              <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px" }}>
                Version {APP_VERSION_STRING} · {latest.date}
              </div>
            </div>
          </div>
          <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", lineHeight: 1.6, marginTop: "8px" }}>
            {latest.title}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: "0 4px" }}>
          <Tabs activeKey={activeTab} onChange={key => this.setState({ activeTab: key })}
            tabBarStyle={{ padding: "0 24px", marginBottom: 0 }}>
            <TabPane tab={<span><BellOutlined /> What&apos;s New</span>} key="whats_new">
              <div style={{ padding: "20px 24px 0" }}>{this.renderWhatsNew()}</div>
            </TabPane>
            <TabPane tab={<span><ClearOutlined /> Clear Cache</span>} key="cache">
              <div style={{ padding: "20px 24px 0" }}>{this.renderCacheHelp()}</div>
            </TabPane>
          </Tabs>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px 20px", display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #E2E8F0", marginTop: "16px" }}>
          <Button onClick={() => this.setState({ activeTab: "cache" })}
            style={{ borderRadius: "50px", borderColor: "#E2E8F0", color: "#718096" }}>
            <ClearOutlined /> Seeing old content?
          </Button>
          <Button type="primary" onClick={this.handleClose}
            style={{ background: NAVY, borderColor: NAVY, borderRadius: "50px", fontWeight: 700 }}>
            Got it — Let&apos;s go! 🚀
          </Button>
        </div>
      </Modal>
    );
  }
}

export default VersionChecker;
